import { useNavigate } from "react-router-dom";
import { ArrowRight, Store } from "lucide-react";
import { formatCurrency } from "@veorest/utils";
import { clientDetailPath, ROUTES } from "../../routes";
import { usePartnerClientsOverview } from "../../hooks/usePartnerReports";
import { formatAgo } from "../../lib/partnerTime";
import { useI18n } from "../../i18n";

const MAX_ROWS = 6;

/**
 * Overview — active clients panel.
 * Lifetime directory numbers (no date range): live load first, then most recent.
 */
export function PartnerActiveClients() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const clientsQuery = usePartnerClientsOverview();
  const clients = clientsQuery.data ?? [];

  const rows = [...clients]
    .sort((a, b) => {
      if (a.active_orders_count !== b.active_orders_count) {
        return b.active_orders_count - a.active_orders_count;
      }
      const aTime = a.last_order_at ? new Date(a.last_order_at).getTime() : 0;
      const bTime = b.last_order_at ? new Date(b.last_order_at).getTime() : 0;
      if (aTime !== bTime) return bTime - aTime;
      return a.name.localeCompare(b.name, locale);
    })
    .slice(0, MAX_ROWS);

  const loading = clientsQuery.isLoading && !clientsQuery.data;

  return (
    <section className="overview-panel">
      <header className="overview-panel__header">
        <span className="overview-panel__icon" aria-hidden>
          <Store className="h-4 w-4" strokeWidth={2.25} />
        </span>
        <div className="min-w-0">
          <h3 className="overview-panel__title">
            {t("overview.activeClients.title")}
          </h3>
          <p className="overview-panel__subtitle">
            {t("overview.activeClients.subtitle")}
          </p>
        </div>
        <button
          type="button"
          className="overview-panel__link"
          onClick={() => navigate(ROUTES.CLIENTS)}
        >
          {t("overview.activeClients.viewAll")}
          <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" strokeWidth={2.5} />
        </button>
      </header>

      <div className="overview-panel__body">
        {loading ? (
          <div className="h-24 rounded-xl skeleton" />
        ) : rows.length === 0 ? (
          <div className="overview-panel-empty">
            <span className="overview-panel-empty__icon" aria-hidden>
              <Store className="h-6 w-6" strokeWidth={2} />
            </span>
            <p className="overview-panel-empty__title">
              {t("overview.activeClients.empty")}
            </p>
          </div>
        ) : (
          <ul className="overview-list">
            {rows.map((client) => {
              const lastOrder = client.last_order_at
                ? formatAgo(
                    locale,
                    Date.now() - new Date(client.last_order_at).getTime(),
                    t("overview.activeClients.justNow")
                  )
                : null;
              return (
                <li key={client.id}>
                  <button
                    type="button"
                    onClick={() => navigate(clientDetailPath(client.id))}
                    className="overview-list-row"
                  >
                    <div
                      className="overview-list-row__avatar"
                      style={{
                        background:
                          "color-mix(in srgb, var(--accent) 14%, transparent)",
                        color: "var(--accent)",
                      }}
                      aria-hidden
                    >
                      <Store className="h-4 w-4" strokeWidth={2.25} />
                    </div>
                    <div className="overview-list-row__main min-w-0">
                      <p className="overview-list-row__title">{client.name}</p>
                      <p className="overview-list-row__meta">
                        {t("overview.activeClients.ordersCount", {
                          count: client.orders_count,
                        })}
                        {lastOrder
                          ? ` · ${lastOrder}`
                          : ` · ${t("overview.activeClients.noOrders")}`}
                      </p>
                    </div>
                    <div className="overview-list-row__end">
                      {client.active_orders_count > 0 ? (
                        <span className="overview-open-badge">
                          {t("overview.activeClients.openCount", {
                            count: client.active_orders_count,
                          })}
                        </span>
                      ) : null}
                      {client.outstanding_pending > 0 ? (
                        <span className="overview-list-row__time overview-list-row__due">
                          {formatCurrency(
                            client.outstanding_pending,
                            client.currency || "IQD",
                            locale
                          )}
                        </span>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
